import { Cross2Icon } from '@radix-ui/react-icons';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrencyFormatter } from '../../../../../app/hooks/useCurrencyFormatter';
import { Button } from '../../../Button';
import DateInput from '../../../DateInput';
import { Input } from '../../../Input';
import MultiSelect from '../../../MultiSelect';
import Select from '../../../Select';
import Switch from '../../../Switch';
import { useRegisterSaleModalController } from './useRegisterSaleModalController';

interface IRegisterSaleModalProps {
  leadId: string;
}

export default function RegisterSaleModal({ leadId }: IRegisterSaleModalProps) {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrencyFormatter();
  const {
    register,
    control,
    errors,
    itemsError,
    watchedInstallmentsCount,
    watchedTotalPrice,
    customizeInstallments,
    installmentRows,
    installmentsSum,
    installmentsError,
    handleToggleCustomizeInstallments,
    handleInstallmentRowChange,
    registerSaleIsPending,
    serviceOptions,
    packageOptions,
    selectedServices,
    selectedPackages,
    services,
    servicePackages,
    handleServiceSelectionChange,
    handlePackageSelectionChange,
    handleServiceQuantityChange,
    handlePackageQuantityChange,
    handleRemoveService,
    handleRemovePackage,
    handleTotalPriceChange,
    getServiceSubtotal,
    getPackageSubtotal,
    handleSubmit,
    handleClose,
    paymentMethodOptions,
  } = useRegisterSaleModalController(leadId);

  return (
    <form
      className="my-4 flex w-full flex-col gap-4"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="flex flex-col gap-1">
        <span className="text-text-secondary text-sm font-medium">
          {t('sale.registerModal.sections.services')}
        </span>
        <MultiSelect
          options={serviceOptions}
          value={selectedServices.map((s) => s.id)}
          onChange={handleServiceSelectionChange}
          placeholder={t('sale.registerModal.fields.servicesPlaceholder')}
          searchPlaceholder={t(
            'sale.registerModal.fields.servicesSearchPlaceholder',
          )}
          noResultsText={t('sale.registerModal.fields.servicesNoResults')}
        />
      </div>

      {selectedServices.length > 0 && (
        <ul className="flex flex-col gap-2">
          {selectedServices.map((sel) => {
            const svc = services.find((s) => s.id === sel.id);
            return (
              <li
                key={sel.id}
                className="bg-surface-card border-border-default flex items-center gap-3 rounded border p-2"
              >
                <span className="text-text-primary min-w-0 flex-1 truncate text-sm">
                  {svc?.name ?? sel.id}
                </span>
                <Input
                  name={`svc-qty-${sel.id}`}
                  type="number"
                  inputMode="numeric"
                  placeholder={t(
                    'sale.registerModal.fields.quantityPlaceholder',
                  )}
                  className="w-20"
                  min={1}
                  value={sel.quantity}
                  onChange={(e) =>
                    handleServiceQuantityChange(
                      sel.id,
                      Math.max(1, parseInt(e.target.value, 10) || 1),
                    )
                  }
                />
                <span className="text-text-secondary w-24 text-right text-sm">
                  {getServiceSubtotal(sel.id, sel.quantity)}
                </span>
                <button
                  type="button"
                  aria-label={t('sale.registerModal.aria.removeService')}
                  onClick={() => handleRemoveService(sel.id)}
                  className="text-text-tertiary hover:text-red-500"
                >
                  <Cross2Icon className="size-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col gap-1">
        <span className="text-text-secondary text-sm font-medium">
          {t('sale.registerModal.sections.packages')}
        </span>
        <MultiSelect
          options={packageOptions}
          value={selectedPackages.map((p) => p.id)}
          onChange={handlePackageSelectionChange}
          placeholder={t('sale.registerModal.fields.packagesPlaceholder')}
          searchPlaceholder={t(
            'sale.registerModal.fields.packagesSearchPlaceholder',
          )}
          noResultsText={t('sale.registerModal.fields.packagesNoResults')}
        />
      </div>

      {selectedPackages.length > 0 && (
        <ul className="flex flex-col gap-2">
          {selectedPackages.map((sel) => {
            const pkg = servicePackages.find((p) => p.id === sel.id);
            return (
              <li
                key={sel.id}
                className="bg-surface-card border-border-default flex items-center gap-3 rounded border p-2"
              >
                <span className="text-text-primary min-w-0 flex-1 truncate text-sm">
                  {pkg?.name ?? sel.id}
                </span>
                <Input
                  name={`pkg-qty-${sel.id}`}
                  type="number"
                  inputMode="numeric"
                  placeholder={t(
                    'sale.registerModal.fields.quantityPlaceholder',
                  )}
                  className="w-20"
                  min={1}
                  value={sel.quantity}
                  onChange={(e) =>
                    handlePackageQuantityChange(
                      sel.id,
                      Math.max(1, parseInt(e.target.value, 10) || 1),
                    )
                  }
                />
                <span className="text-text-secondary w-24 text-right text-sm">
                  {getPackageSubtotal(sel.id, sel.quantity)}
                </span>
                <button
                  type="button"
                  aria-label={t('sale.registerModal.aria.removePackage')}
                  onClick={() => handleRemovePackage(sel.id)}
                  className="text-text-tertiary hover:text-red-500"
                >
                  <Cross2Icon className="size-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {itemsError && (
        <p className="text-xs text-red-500 dark:text-red-400">{itemsError}</p>
      )}

      <Input
        type="number"
        inputMode="decimal"
        placeholder={t('sale.registerModal.fields.totalPrice')}
        step="0.01"
        min="0"
        {...register('totalPrice', {
          valueAsNumber: true,
          onChange: handleTotalPriceChange,
        })}
        error={errors.totalPrice?.message}
        disabled={registerSaleIsPending}
      />

      <Controller
        name="paymentMethod"
        control={control}
        render={({ field }) => (
          <Select
            placeholder={t('sale.registerModal.fields.paymentMethod')}
            options={paymentMethodOptions}
            value={field.value}
            onChange={field.onChange}
            error={errors.paymentMethod?.message}
            disabled={registerSaleIsPending}
          />
        )}
      />

      <Input
        type="number"
        inputMode="numeric"
        placeholder={t('sale.registerModal.fields.installmentsCount')}
        min={1}
        max={24}
        {...register('installmentsCount', { valueAsNumber: true })}
        error={errors.installmentsCount?.message}
        disabled={registerSaleIsPending}
      />

      {watchedInstallmentsCount > 1 && (
        <Controller
          name="firstDueDate"
          control={control}
          render={({ field }) => (
            <DateInput
              name="firstDueDate"
              value={field.value}
              onChange={field.onChange}
              placeholder={t('sale.registerModal.fields.firstDueDate')}
              error={errors.firstDueDate?.message}
              disabled={registerSaleIsPending}
            />
          )}
        />
      )}

      {watchedInstallmentsCount > 1 && (
        <div className="border-border-default flex flex-col gap-3 rounded border p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-text-secondary text-sm font-medium">
              {t('sale.registerModal.installments.customizeLabel')}
            </span>
            <Switch
              id="customize-installments"
              name="customizeInstallments"
              checked={customizeInstallments}
              onCheckedChange={handleToggleCustomizeInstallments}
              disabled={registerSaleIsPending}
              aria-label={t('sale.registerModal.installments.customizeLabel')}
            />
          </div>

          {customizeInstallments && (
            <>
              <ul className="flex flex-col gap-2">
                {installmentRows.map((row, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="text-text-tertiary w-6 text-xs">
                      {index + 1}
                    </span>
                    <div className="w-28">
                      <Input
                        name={`installment-amount-${index}`}
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder={t(
                          'sale.registerModal.installments.amount',
                        )}
                        value={row.amount}
                        onChange={(event) =>
                          handleInstallmentRowChange(
                            index,
                            'amount',
                            event.target.value,
                          )
                        }
                        disabled={registerSaleIsPending}
                      />
                    </div>
                    <div className="flex-1">
                      <DateInput
                        name={`installment-due-${index}`}
                        value={row.dueDate}
                        onChange={(value) =>
                          handleInstallmentRowChange(index, 'dueDate', value)
                        }
                        placeholder={t(
                          'sale.registerModal.installments.dueDate',
                        )}
                        disabled={registerSaleIsPending}
                      />
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between text-xs">
                <span className="text-text-tertiary">
                  {t('sale.registerModal.installments.sum')}
                </span>
                <span className="text-text-secondary">
                  {formatCurrency(Math.round(installmentsSum * 100))} /{' '}
                  {formatCurrency(Math.round((watchedTotalPrice || 0) * 100))}
                </span>
              </div>

              {installmentsError && (
                <p className="text-xs text-red-500 dark:text-red-400">
                  {installmentsError}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <Controller
        name="soldAt"
        control={control}
        render={({ field }) => (
          <DateInput
            name="soldAt"
            value={field.value}
            onChange={field.onChange}
            placeholder={t('sale.registerModal.fields.soldAt')}
            error={errors.soldAt?.message}
            disabled={registerSaleIsPending}
          />
        )}
      />

      <Input
        placeholder={t('sale.registerModal.fields.notes')}
        {...register('notes')}
        error={errors.notes?.message}
        disabled={registerSaleIsPending}
      />

      <section className="mt-2 flex gap-4">
        <Button
          className="w-40"
          color="ghost"
          disabled={registerSaleIsPending}
          onClick={handleClose}
        >
          {t('sale.registerModal.actions.cancel')}
        </Button>
        <Button
          type="submit"
          isLoading={registerSaleIsPending}
          disabled={registerSaleIsPending}
        >
          {t('sale.registerModal.actions.register')}
        </Button>
      </section>
    </form>
  );
}
